import React, { useState, useEffect } from "react";
import { Edit2, Trash2, CreditCard, Smartphone, CheckCircle2, Plus } from "lucide-react";
import { ConfigSection } from "../ui/ConfigSection";
import { Button } from "../../../components/ui";
import { BankAccountModal } from "../modals/BankAccountModal";
import { DigitalPaymentModal } from "../modals/DigitalPaymentModal";
import { useBilling } from "../hooks/useBilling";

/**
 * BillingTab - Refactored to use useBilling hook.
 */
export default function BillingTab() {
    const { banks, digitalPayments, loading, fetchData, deleteBank, deleteDigitalPayment } = useBilling();

    // Modal states
    const [bankModalOpen, setBankModalOpen] = useState(false);
    const [digitalModalOpen, setDigitalModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        const handleAddBank = () => {
            setEditingItem(null);
            setBankModalOpen(true);
        };
        const handleAddDigital = () => {
            setEditingItem(null);
            setDigitalModalOpen(true);
        };

        window.addEventListener('system-config:add-bank', handleAddBank);
        window.addEventListener('system-config:add-digital', handleAddDigital);

        return () => {
            window.removeEventListener('system-config:add-bank', handleAddBank);
            window.removeEventListener('system-config:add-digital', handleAddDigital);
        };
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl space-y-8 animate-in fade-in duration-500 pb-20">
            
            {/* 1. Bank Accounts Section */}
            <ConfigSection 
                title="Corporate Bank Accounts" 
                description="Manage multiple accounts for diverse payment settlement."
                dotColor="bg-blue-500"
            >
                <div className="flex justify-end mb-4 -mt-12">
                    <Button
                        variant="orange"
                        leftIcon={<Plus size={14} />}
                        onClick={() => { setEditingItem(null); setBankModalOpen(true); }}
                        className="shadow-md shadow-orange-100/50 rounded-lg h-[32px] text-[11px]"
                    >
                        Add Bank Account
                    </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2 items-start">
                    {banks.map((acc) => (
                        <div key={acc._id} className="card p-5 border border-slate-200 hover:border-brand-blue/50 transition-all group bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700">
                                    <CreditCard size={20} strokeWidth={2.5} />
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button 
                                        variant="secondary" 
                                        size="icon-sm" 
                                        onClick={() => { setEditingItem(acc); setBankModalOpen(true); }}
                                        className="h-[30px] w-[30px]"
                                    >
                                        <Edit2 size={14} />
                                    </Button>
                                    <Button 
                                        variant="secondary" 
                                        size="icon-sm" 
                                        onClick={() => deleteBank(acc._id)}
                                        className="h-[30px] w-[30px] hover:text-red-500 hover:bg-red-50"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center gap-1.5 mb-1.5">
                                <h4 className="font-semibold text-slate-900 text-[14px] tracking-tight truncate uppercase">{acc.bankName}</h4>
                                {acc.isPrimary && <CheckCircle2 size={12} className="text-emerald-500" />}
                            </div>
                            
                            <p className="text-[12px] text-slate-500 mb-2 font-normal tracking-tight line-clamp-1">{acc.accountName}</p>

                            <div className="space-y-1 bg-slate-50/80 p-3 rounded-xl">
                                {acc.vendor && (
                                    <div className="flex justify-between text-[11px] gap-2">
                                        <span className="text-slate-400 shrink-0">Vendor:</span>
                                        <span className="text-slate-700 text-right">
                                            {typeof acc.vendor === 'object' ? (acc.vendor.vendorName || acc.vendor.name || acc.vendor.title) : acc.vendor}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between text-[11px] gap-2">
                                    <span className="text-slate-400 shrink-0">Acc Number:</span>
                                    <span className="text-slate-700 text-right break-all">{acc.accountNumber}</span>
                                </div>
                                <div className="flex justify-between text-[11px] gap-2">
                                    <span className="text-slate-400 shrink-0">IFSC Code:</span>
                                    <span className="text-slate-700 text-right">{acc.ifscCode}</span>
                                </div>
                                {acc.branch && (
                                    <div className="flex justify-between text-[11px] gap-2">
                                        <span className="text-slate-400 shrink-0">Branch:</span>
                                        <span className="text-slate-700 ml-2 text-right line-clamp-2">{acc.branch}</span>
                                    </div>
                                )}
                                {acc.upiId && (
                                    <div className="flex justify-between text-[11px] gap-2">
                                        <span className="text-slate-400 shrink-0">UPI/Merchant ID:</span>
                                        <span className="text-slate-700 ml-2 text-right break-all">{acc.upiId}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </ConfigSection>

            {/* 2. Digital Payments Section */}
            {/*
            <ConfigSection 
                title="Digital Payment Methods" 
                description="Consolidate UPI IDs and Merchant payment handles."
                dotColor="bg-emerald-500"
            >
                <div className="flex justify-end mb-4 -mt-12">
                    <Button
                        variant="orange"
                        leftIcon={<Plus size={14} />}
                        onClick={() => { setEditingItem(null); setDigitalModalOpen(true); }}
                        className="shadow-md shadow-orange-100/50 rounded-lg h-[32px] text-[11px]"
                    >
                        Add Digital Account
                    </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                    {digitalPayments.map((pay) => (
                        <div key={pay._id} className="card p-5 border border-slate-200 hover:border-brand-blue/50 transition-all group bg-white">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700">
                                    <Smartphone size={20} strokeWidth={2.5} />
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button 
                                        variant="secondary" 
                                        size="icon-sm" 
                                        onClick={() => { setEditingItem(pay); setDigitalModalOpen(true); }}
                                        className="h-[30px] w-[30px]"
                                    >
                                        <Edit2 size={14} />
                                    </Button>
                                    <Button 
                                        variant="secondary" 
                                        size="icon-sm" 
                                        onClick={() => deleteDigitalPayment(pay._id)}
                                        className="h-[30px] w-[30px] hover:text-red-500 hover:bg-red-50"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center gap-1.5 mb-1.5">
                                <h4 className="font-semibold text-slate-900 text-[14px] tracking-tight truncate uppercase">{pay.providerName}</h4>
                                {pay.isPrimary && <CheckCircle2 size={12} className="text-emerald-500" />}
                            </div>
                            
                            <p className="text-[12px] text-slate-500 mb-4 font-normal tracking-tight line-clamp-1">{pay.displayName}</p>

                            <div className="flex items-center gap-2.5 text-[12px] text-brand-blue font-bold bg-blue-50/50 px-3.5 py-1.5 rounded-xl border border-blue-100/50">
                                <Smartphone size={12} />
                                {pay.upiId}
                            </div>
                        </div>
                    ))}
                </div>
            </ConfigSection>
            */}

            <BankAccountModal isOpen={bankModalOpen} onClose={() => setBankModalOpen(false)} onSuccess={fetchData} initialData={editingItem} />
            <DigitalPaymentModal isOpen={digitalModalOpen} onClose={() => setDigitalModalOpen(false)} onSuccess={fetchData} initialData={editingItem} />
        </div>
    );
}
